/**
 * Helper functions for screener data transformation and formatting
 */

/**
 * Group flat results array by ticker symbol
 * @param {Array} results - Array of contract objects
 * @returns {Object} - Object with ticker keys and contract arrays as values
 */
export const groupResultsByTicker = (results) => {
  if (!Array.isArray(results)) return {};

  return results.reduce((acc, contract) => {
    const ticker = contract.Ticker || contract.ticker;
    if (!ticker) return acc;

    if (!acc[ticker]) {
      acc[ticker] = [];
    }
    acc[ticker].push(contract);
    return acc;
  }, {});
};

/**
 * Calculate average gain percentage for array of contracts
 * @param {Array} contracts - Array of contract objects
 * @returns {number} - Average premium gain percentage
 */
export const calculateAvgGain = (contracts) => {
  if (!Array.isArray(contracts) || contracts.length === 0) return 0;

  const sum = contracts.reduce((acc, contract) => {
    const gain = contract['Premium % Gain'] || contract.premiumGain || 0;
    return acc + gain;
  }, 0);

  return sum / contracts.length;
};

/**
 * Extract unique expiration dates from results
 * @param {Array} results - Array of contract objects
 * @returns {Array} - Sorted array of unique expiration dates
 */
export const extractUniqueExpirations = (results) => {
  if (!Array.isArray(results)) return [];

  const expirations = new Set(
    results.map(r => r.Expiration || r.expiration).filter(Boolean)
  );

  return Array.from(expirations).sort();
};

/**
 * Filter contracts by selected expiration dates
 * @param {Array} contracts - Array of contract objects
 * @param {Array} selectedExpirations - Array of expiration date strings
 * @returns {Array} - Filtered contracts
 */
export const filterContractsByExpiration = (contracts, selectedExpirations) => {
  if (!Array.isArray(contracts)) return [];
  if (!selectedExpirations || selectedExpirations.length === 0) return contracts;

  return contracts.filter(contract => {
    const expiration = contract.Expiration || contract.expiration;
    return selectedExpirations.includes(expiration);
  });
};

/**
 * Calculate days until expiration
 * @param {string} expirationDate - Date string in YYYY-MM-DD format
 * @returns {number} - Days until expiration (minimum 1)
 */
export const calculateDaysUntilExpiration = (expirationDate) => {
  if (!expirationDate) return 90;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expDate = new Date(expirationDate);
  expDate.setHours(0, 0, 0, 0);

  const diffTime = expDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays < 1 ? 1 : diffDays;
};

/**
 * Format date to short readable string (e.g., "Jan 16, 2026")
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export const formatDateShort = (dateStr) => {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return dateStr;
  }
};

/**
 * Format date to full readable string (e.g., "Fri, Jan 17, 2025")
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export const formatDateFull = (dateStr) => {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return dateStr;
  }
};

/**
 * Format currency value
 * @param {number} value - Numeric value
 * @returns {string} - Formatted currency string (e.g., "$15.50")
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '$0.00';
  return `$${Number(value).toFixed(2)}`;
};

/**
 * Format percentage value
 * @param {number} value - Numeric value
 * @returns {string} - Formatted percentage string (e.g., "+15.5%")
 */
export const formatPercentage = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0.0%';
  const sign = value > 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(1)}%`;
};

/**
 * Format large numbers with commas
 * @param {number} value - Numeric value
 * @returns {string} - Formatted number string (e.g., "1,234")
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
};
